import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import { Plus, Trash2, Edit, Sparkles, BookOpen, UserCheck, Link as LinkIcon, Check, FileText } from "lucide-react";
import { RoteiroConfigEditor, DEFAULT_ROTEIRO_CONFIG } from "@/components/RoteiroConfigEditor";
import { CompetitionLogoField } from "@/components/CompetitionLogoField";

const empty = {
  nome: "",
  data: new Date().toISOString().slice(0, 10),
  horario_inicio: "18:00",
  promotor: "",
  responsavel: "",
  diretoria: "",
  local: "",
  num_arbitros: 5,
  judge_ids: [],
  admin_is_judge: false,
  formato: "direto",
  intervalo_previa_final_min: 30,
  roteiro_config: DEFAULT_ROTEIRO_CONFIG,
  library_ids: [], // categorias da biblioteca a serem adicionadas
};

const FORMATO_LABEL = { direto: "Direto", previa_final: "Prévia e Final" };

export default function Competitions() {
  const { competitions, refresh, setCurrent } = useCompetition();
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [library, setLibrary] = useState([]);
  const [showLib, setShowLib] = useState(false);
  const [libSelected, setLibSelected] = useState({}); // id -> true
  const [libNewName, setLibNewName] = useState("");
  const [libTargetCid, setLibTargetCid] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [libFilter, setLibFilter] = useState("");
  const [importableCount, setImportableCount] = useState(0);
  const [importing, setImporting] = useState(false);
  const [eventStarted, setEventStarted] = useState(false);

  const publicOrigin = window.location.origin;
  const buildRegLink = (cid) => `${publicOrigin}/inscricao/${cid}`;

  const exportEventReport = (cid) => {
    if (!cid) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/competitions/${cid}/event-report.pdf`;
    window.open(url, "_blank");
  };

  const filteredLibrary = library.filter((l) =>
    !libFilter || (l.nome || "").toLowerCase().includes(libFilter.toLowerCase())
  );

  const toggleFormLib = (lid) => {
    setForm((f) => {
      const has = (f.library_ids || []).includes(lid);
      return {
        ...f,
        library_ids: has
          ? f.library_ids.filter((x) => x !== lid)
          : [...(f.library_ids || []), lid],
      };
    });
  };

  const copyRegLink = async (cid) => {
    const url = buildRegLink(cid);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // fallback: prompt-based copy
      window.prompt("Copie o link de inscrição:", url);
    }
    setCopiedId(cid);
    setTimeout(() => setCopiedId((v) => (v === cid ? null : v)), 2000);
  };

  const judges = users.filter((u) => u.role === "judge");

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch {
      setUsers([]);
    }
  };
  const loadLibrary = async () => {
    try {
      const { data } = await api.get("/category-library");
      setLibrary(data);
    } catch {
      setLibrary([]);
    }
    try {
      const { data } = await api.get("/category-library/importable");
      setImportableCount(data?.count || 0);
    } catch {
      setImportableCount(0);
    }
  };

  const importFromCompetitions = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/category-library/import-from-competitions");
      await loadLibrary();
      window.alert(
        `Importadas ${data.added} categoria(s) para a biblioteca. ${data.skipped} já existiam.`
      );
    } catch (e) {
      window.alert(extractErrorMessage(e));
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadLibrary();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // If editing and start time changed vs saved, confirm before recalc
      if (editingId) {
        const existing = competitions.find((c) => c.id === editingId);
        if (
          existing &&
          existing.horario_inicio &&
          form.horario_inicio &&
          existing.horario_inicio !== form.horario_inicio
        ) {
          const ok = window.confirm(
            `O horário será alterado de ${existing.horario_inicio} para ${form.horario_inicio}. ` +
              "Isso recalcula todo o roteiro. Confirma?"
          );
          if (!ok) return;
        }
        await api.put(`/competitions/${editingId}`, form);
        // Aplicar biblioteca também na edição se algum item foi marcado
        if ((form.library_ids || []).length > 0) {
          await api.post(`/competitions/${editingId}/categories/from-library`, {
            library_ids: form.library_ids,
          });
        }
      } else {
        const { data } = await api.post("/competitions", form);
        setCurrent(data.id);
        // Aplicar biblioteca ao novo campeonato
        if ((form.library_ids || []).length > 0) {
          await api.post(`/competitions/${data.id}/categories/from-library`, {
            library_ids: form.library_ids,
          });
        }
      }
      setForm(empty);
      setEditingId(null);
      await refresh();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const seedDemo = async () => {
    if (!window.confirm("Criar uma competição 'COPA DE DEMONSTRAÇÃO' completa com 10 categorias e ~50 atletas?"))
      return;
    setError("");
    try {
      const { data } = await api.post("/competitions/seed-demo");
      setCurrent(data.competition_id);
      await refresh();
      window.alert("Dados de demonstração criados!");
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setEventStarted(false);
    api.get(`/competitions/${c.id}`).then((r) => setEventStarted(!!r.data.event_started)).catch(() => {});
    setForm({
      nome: c.nome,
      data: c.data,
      horario_inicio: c.horario_inicio || "18:00",
      promotor: c.promotor || "",
      responsavel: c.responsavel || "",
      diretoria: c.diretoria || "",
      local: c.local || "",
      num_arbitros: c.num_arbitros,
      judge_ids: c.judge_ids || [],
      admin_is_judge: !!c.admin_is_judge,
      formato: c.formato || "direto",
      intervalo_previa_final_min: c.intervalo_previa_final_min ?? 30,
      roteiro_config: { ...DEFAULT_ROTEIRO_CONFIG, ...(c.roteiro_config || {}) },
      library_ids: [], // seleção da biblioteca é sempre limpa ao editar
    });
  };

  const del = async (id) => {
    if (!window.confirm("Excluir competição e TODOS os dados relacionados?")) return;
    await api.delete(`/competitions/${id}`);
    await refresh();
  };

  const toggleJudge = (uid) => {
    setForm((f) => {
      const has = (f.judge_ids || []).includes(uid);
      return {
        ...f,
        judge_ids: has
          ? f.judge_ids.filter((x) => x !== uid)
          : [...(f.judge_ids || []), uid],
      };
    });
  };

  const openLib = (cid) => {
    setLibTargetCid(cid);
    setLibSelected({});
    setShowLib(true);
  };
  const createLibItem = async (e) => {
    e.preventDefault();
    if (!libNewName.trim()) return;
    try {
      await api.post("/category-library", {
        nome: libNewName.trim(),
        tempo_minutos: 5,
      });
      setLibNewName("");
      await loadLibrary();
    } catch (e) {
      window.alert(extractErrorMessage(e));
    }
  };
  const deleteLibItem = async (lid) => {
    if (!window.confirm("Excluir da biblioteca? A competição não é afetada.")) return;
    await api.delete(`/category-library/${lid}`);
    await loadLibrary();
  };
  const applyLibrary = async () => {
    const ids = Object.keys(libSelected).filter((k) => libSelected[k]);
    if (!ids.length || !libTargetCid) {
      setShowLib(false);
      return;
    }
    try {
      const { data } = await api.post(
        `/competitions/${libTargetCid}/categories/from-library`,
        { library_ids: ids }
      );
      window.alert(`Adicionadas ${data.added} categoria(s). ${data.skipped} já existiam.`);
      setShowLib(false);
    } catch (e) {
      window.alert(extractErrorMessage(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Configuração</div>
          <h1 className="font-display text-5xl font-black tracking-tight">COMPETIÇÕES</h1>
        </div>
        <button
          data-testid="seed-demo-btn"
          className="btn-ghost flex items-center gap-2"
          onClick={seedDemo}
        >
          <Sparkles size={16} /> Gerar Dados de Teste
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="card p-5">
            <div className="overline mb-3">
              {editingId ? "Editar Competição" : "Nova Competição"}
            </div>
            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="comp-nome"
                className="input-field"
                placeholder="Nome do evento"
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="overline block mb-1">Data</label>
                  <input
                    data-testid="comp-data"
                    type="date"
                    className="input-field"
                    required
                    value={form.data}
                    onChange={(e) => setForm({ ...form, data: e.target.value })}
                  />
                </div>
                <div>
                  <label className="overline block mb-1">Horário de Início</label>
                  <input
                    data-testid="comp-horario"
                    type="time"
                    className="input-field"
                    required
                    value={form.horario_inicio}
                    onChange={(e) => setForm({ ...form, horario_inicio: e.target.value })}
                  />
                </div>
              </div>
              <input
                data-testid="comp-local"
                className="input-field"
                placeholder="Local"
                value={form.local}
                onChange={(e) => setForm({ ...form, local: e.target.value })}
              />
              <input
                data-testid="comp-promotor"
                className="input-field"
                placeholder="Promotor"
                value={form.promotor}
                onChange={(e) => setForm({ ...form, promotor: e.target.value })}
              />
              <input
                data-testid="comp-responsavel"
                className="input-field"
                placeholder="Responsável"
                value={form.responsavel}
                onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
              />
              <input
                data-testid="comp-diretoria"
                className="input-field"
                placeholder="Diretoria"
                value={form.diretoria}
                onChange={(e) => setForm({ ...form, diretoria: e.target.value })}
              />
              <div>
                <label className="overline block mb-1">Nº total de árbitros</label>
                <input
                  data-testid="comp-arbitros"
                  type="number"
                  min={1}
                  className="input-field"
                  value={form.num_arbitros}
                  onChange={(e) =>
                    setForm({ ...form, num_arbitros: parseInt(e.target.value || "1") })
                  }
                />
                <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                  Se você votar como Árbitro Central, este total já inclui você.
                </div>
              </div>

              <div className="border border-white/10 p-3 space-y-2" data-testid="comp-formato-box">
                <label className="overline block flex items-center gap-2">
                  Formato da competição
                  {editingId && eventStarted && (
                    <span className="text-[9px] text-[#C9A84C]" title="Evento já começou">🔒</span>
                  )}
                </label>
                <select
                  data-testid="comp-formato"
                  className="input-field"
                  value={form.formato}
                  disabled={editingId && eventStarted}
                  onChange={(e) => setForm({ ...form, formato: e.target.value })}
                >
                  <option value="direto">Direto — cada categoria faz prévia e final antes da próxima</option>
                  <option value="previa_final">Prévia e Final — todas as prévias → intervalo → todas as finais</option>
                </select>
                {form.formato === "previa_final" && (
                  <div>
                    <label className="overline block mb-1">Intervalo prévias → finais (min)</label>
                    <input
                      data-testid="comp-intervalo-pf"
                      type="number"
                      min={0}
                      className="input-field"
                      value={form.intervalo_previa_final_min}
                      onChange={(e) =>
                        setForm({ ...form, intervalo_previa_final_min: parseInt(e.target.value || "0") })
                      }
                    />
                  </div>
                )}
                <div className="text-[11px] text-[var(--text-secondary)]" data-testid="comp-formato-hint">
                  {editingId && eventStarted
                    ? "O evento já começou (Maestro avançou ou há notas lançadas): o formato não pode mais ser alterado."
                    : form.formato === "previa_final"
                    ? "O roteiro, o Maestro e o Backstage seguem: PRÉVIAS de todas as categorias → INTERVALO → FINAIS. O tempo por atleta da categoria vale para cada fase."
                    : "Fluxo clássico: cada categoria entra uma vez e faz prévia + final completo antes da próxima."}
                </div>
              </div>

              <CompetitionLogoField
                competitionId={editingId}
                logoUrl={competitions.find((c) => c.id === editingId)?.logo_url}
                onChanged={refresh}
              />

              <RoteiroConfigEditor
                value={form.roteiro_config}
                onChange={(roteiro_config) => setForm({ ...form, roteiro_config })}
              />

              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  data-testid="comp-admin-is-judge"
                  type="checkbox"
                  checked={form.admin_is_judge}
                  onChange={(e) => setForm({ ...form, admin_is_judge: e.target.checked })}
                />
                <span>
                  Sou <strong>Maestro + Árbitro Central</strong> (minha nota conta)
                </span>
              </label>

              <div>
                <label className="overline block mb-1 flex items-center gap-2">
                  <BookOpen size={12} /> Categorias da Biblioteca
                </label>
                <input
                  data-testid="comp-lib-filter"
                  className="input-field mb-2 text-sm"
                  placeholder="Digite para buscar…"
                  value={libFilter}
                  onChange={(e) => setLibFilter(e.target.value)}
                  autoComplete="off"
                />
                <div
                  className="border border-white/10 rounded-sm max-h-48 overflow-y-auto"
                  data-testid="comp-lib-list"
                >
                  {library.length === 0 && (
                    <div className="p-3 text-xs text-[var(--text-secondary)]">
                      Biblioteca vazia. Adicione categorias em uma competição existente (ícone livro na tabela).
                    </div>
                  )}
                  {library.length > 0 && filteredLibrary.length === 0 && (
                    <div className="p-3 text-xs text-[var(--text-secondary)]">
                      Nenhuma categoria com esse nome.
                    </div>
                  )}
                  {filteredLibrary.map((l) => {
                    const checked = (form.library_ids || []).includes(l.id);
                    return (
                      <label
                        key={l.id}
                        className={
                          "flex items-center gap-2 px-3 py-2 border-b border-white/5 cursor-pointer hover:bg-white/5 text-sm " +
                          (checked ? "bg-[#C9A84C]/10" : "")
                        }
                        data-testid={`comp-lib-item-${l.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFormLib(l.id)}
                        />
                        <span className="flex-1 truncate">{l.nome}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                  {(form.library_ids || []).length} selecionada(s).
                  {editingId
                    ? " Serão adicionadas ao editar; existentes não são duplicadas."
                    : " Serão adicionadas ao criar o campeonato."}
                </div>
              </div>

              <div>
                <label className="overline block mb-1 flex items-center gap-2">
                  <UserCheck size={12} /> Árbitros Escalados
                </label>
                <div
                  className="border border-white/10 rounded-sm max-h-40 overflow-y-auto"
                  data-testid="comp-judges-list"
                >
                  {judges.length === 0 && (
                    <div className="p-3 text-xs text-[var(--text-secondary)]">
                      Cadastre árbitros em <em>Usuários</em> primeiro.
                    </div>
                  )}
                  {judges.map((j) => {
                    const checked = (form.judge_ids || []).includes(j.id);
                    return (
                      <label
                        key={j.id}
                        className="flex items-center gap-2 px-3 py-2 border-b border-white/5 cursor-pointer hover:bg-white/5 text-sm"
                        data-testid={`comp-judge-${j.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleJudge(j.id)}
                        />
                        <span className="flex-1 truncate">{j.name}</span>
                        <span className="text-[10px] text-[var(--text-secondary)]">
                          {j.email}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                  Cada árbitro só verá este campeonato após ser escalado.
                </div>
              </div>

              {error && (
                <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2">
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <button data-testid="comp-submit" className="btn-primary flex-1">
                  {editingId ? "Atualizar" : "Criar"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setEditingId(null);
                      setEventStarted(false);
                      setForm(empty);
                    }}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card">
            <table className="table-dense">
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Data · Hora</th>
                  <th>Local</th>
                  <th>Árbitros</th>
                  <th className="text-right pr-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {competitions.map((c) => (
                  <tr key={c.id} data-testid={`comp-row-${c.id}`}>
                    <td>
                      <div className="font-medium">{c.nome}</div>
                      <div className="text-xs text-[var(--text-secondary)]">
                        {c.promotor}
                      </div>
                      <div
                        className="text-[10px] text-[#C9A84C] mt-1 truncate max-w-[280px]"
                        data-testid={`reglink-${c.id}`}
                        title={buildRegLink(c.id)}
                      >
                        🔗 {buildRegLink(c.id)}
                      </div>
                    </td>
                    <td className="tabular-nums">
                      {c.data}
                      <div className="text-xs text-[#C9A84C]" data-testid={`comp-horario-${c.id}`}>
                        {c.horario_inicio || "18:00"}
                      </div>
                      <span
                        data-testid={`comp-formato-${c.id}`}
                        className={"chip mt-1 whitespace-nowrap " + (c.formato === "previa_final" ? "chip-admin" : "chip-judge")}
                      >
                        {FORMATO_LABEL[c.formato] || "Direto"}
                      </span>
                    </td>
                    <td>{c.local || "—"}</td>
                    <td className="tabular-nums">
                      {c.num_arbitros}
                      <div className="text-[10px] text-[var(--text-secondary)]">
                        {(c.judge_ids || []).length} escalado(s)
                        {c.admin_is_judge ? " + admin" : ""}
                      </div>
                    </td>
                    <td className="text-right pr-4">
                      <button
                        className="btn-ghost mr-1"
                        title="Copiar link público de inscrição"
                        data-testid={`copy-reglink-${c.id}`}
                        onClick={() => copyRegLink(c.id)}
                      >
                        {copiedId === c.id ? (
                          <Check size={14} style={{ color: "#16FF7A" }} />
                        ) : (
                          <LinkIcon size={14} />
                        )}
                      </button>
                      <button
                        className="btn-ghost mr-1"
                        title="Relatório Oficial do Evento (PDF)"
                        data-testid={`event-report-${c.id}`}
                        onClick={() => exportEventReport(c.id)}
                      >
                        <FileText size={14} />
                      </button>
                      <button
                        className="btn-ghost mr-1"
                        title="Categorias da Biblioteca"
                        data-testid={`open-lib-${c.id}`}
                        onClick={() => openLib(c.id)}
                      >
                        <BookOpen size={14} />
                      </button>
                      <button
                        className="btn-ghost mr-1"
                        data-testid={`edit-comp-${c.id}`}
                        onClick={() => edit(c)}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="btn-ghost"
                        data-testid={`del-comp-${c.id}`}
                        onClick={() => del(c.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {competitions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-[var(--text-secondary)]">
                      Nenhuma competição cadastrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showLib && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowLib(false)}
          data-testid="lib-modal"
        >
          <div
            className="card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline text-[#C9A84C]">Biblioteca Global</div>
                <div className="font-display text-2xl font-black tracking-tight">
                  Categorias Disponíveis
                </div>
                <div className="text-xs text-[var(--text-secondary)] mt-1">
                  {library.length} categoria(s) na biblioteca
                </div>
              </div>
              <button className="btn-ghost text-xs" onClick={() => setShowLib(false)}>
                Fechar
              </button>
            </div>

            {importableCount > 0 && (
              <div
                className="mb-4 p-3 rounded-sm flex items-center gap-3"
                style={{
                  background: "rgba(201,168,76,0.08)",
                  border: "1px solid rgba(201,168,76,0.4)",
                }}
                data-testid="lib-import-banner"
              >
                <BookOpen size={18} style={{ color: "#C9A84C" }} />
                <div className="flex-1 text-sm">
                  <div className="font-bold text-[#C9A84C]">
                    {importableCount} categoria(s) em uso nas suas competições ainda não estão na biblioteca.
                  </div>
                  <div className="text-[var(--text-secondary)] text-xs">
                    Importe todas de uma vez para reutilizar em próximos campeonatos.
                  </div>
                </div>
                <button
                  data-testid="lib-import-btn"
                  className="btn-primary text-xs"
                  disabled={importing}
                  onClick={importFromCompetitions}
                >
                  {importing ? "Importando..." : `Importar ${importableCount}`}
                </button>
              </div>
            )}

            <form onSubmit={createLibItem} className="mb-4">
              <div className="overline mb-2">Adicionar nova categoria manualmente</div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)] block mb-1">
                    Nome da Categoria
                  </label>
                  <input
                    data-testid="lib-new-name"
                    className="input-field w-full"
                    placeholder="Ex: WELLNESS NOVICE"
                    value={libNewName}
                    onChange={(e) => setLibNewName(e.target.value)}
                  />
                </div>
                <button
                  className="btn-primary flex items-center gap-1 h-[42px]"
                  data-testid="lib-new-submit"
                >
                  <Plus size={14} /> Adicionar
                </button>
              </div>
            </form>

            {library.length > 5 && (
              <input
                data-testid="lib-search"
                className="input-field mb-3"
                placeholder="Filtrar biblioteca..."
                value={libFilter}
                onChange={(e) => setLibFilter(e.target.value)}
              />
            )}

            <div className="border border-white/10 rounded-sm max-h-80 overflow-y-auto mb-4">
              {library.length === 0 && (
                <div className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  Biblioteca vazia. Use o botão dourado acima para importar de suas competições
                  existentes, ou crie manualmente no formulário acima.
                </div>
              )}
              {filteredLibrary.map((l) => (
                <label
                  key={l.id}
                  className="flex items-center gap-3 px-3 py-2 border-b border-white/5 hover:bg-white/5 cursor-pointer text-sm"
                  data-testid={`lib-item-${l.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!libSelected[l.id]}
                    onChange={(e) =>
                      setLibSelected({ ...libSelected, [l.id]: e.target.checked })
                    }
                  />
                  <span className="flex-1">{l.nome}</span>
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteLibItem(l.id);
                    }}
                    data-testid={`lib-del-${l.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </label>
              ))}
              {library.length > 0 && filteredLibrary.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--text-secondary)]">
                  Nenhum resultado para "{libFilter}".
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setShowLib(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                data-testid="lib-apply"
                onClick={applyLibrary}
                disabled={!Object.values(libSelected).some(Boolean)}
              >
                Adicionar Selecionadas à Competição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
